// ─────────────────────────────────────────────────────────────────────────────
// Due dates: the label, the group and the overdue flag, all derived from ONE
// stored value and the instant you ask.
//
// ⚠ NEVER STORE A BUCKET OR AN OVERDUE FLAG. They are true only for the moment
// they were computed, and this app stays open in a pocket: a todo due today
// becomes overdue at midnight without a single byte changing. The server used to
// compute all three and send them, which was wrong twice over — stale after
// midnight, and computed against the SERVER's clock.
//
// ⚠ THE DEVICE'S CLOCK IS THE RIGHT ONE, and that is the point rather than a
// convenience. A server in UTC and a phone in Zürich disagree about what "Today"
// means for two hours out of every day. The person reading the screen is the one
// the word is about.
//
// Pure, and `now` is a required argument — never `Date.now()` inside. That keeps
// it testable and, just as importantly, makes a whole list derive against ONE
// instant instead of drifting across a render.
// ─────────────────────────────────────────────────────────────────────────────

import { type DueBucket } from "home/app/mobile/types";
import { currentLanguage, dateLocale, t } from "home/app/mobile/i18n";

export interface DueFacts {
    /** Display text, in the app's language: "" when undated, else "Today" / "Tomorrow" / "29 Aug". */
    label: string;
    bucket: DueBucket;
    overdue: boolean;
}

/**
 * ⚠ In the APP's language, never the phone's locale: a date rendered in the device's own locale
 * would be the one foreign string on a screen written in the account's language. See
 * `app/mobile/i18n.ts`.
 *
 * English keeps its fixed table, so its label stays byte-identical to the one the server used to
 * send; German goes through Intl with `dateLocale()` ("29. Aug.").
 */
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "29 Aug" / "29. Aug." — a short day and month in the app's language. */
export function dayMonthText(date: Date): string {
    if (currentLanguage() === "en") return `${date.getDate()} ${MONTHS[date.getMonth()]}`;

    return new Intl.DateTimeFormat(dateLocale(), { day: "numeric", month: "short" }).format(date);
}

/**
 * A due date as a plain date with its year — the detail screen's control, which shows the date and
 * no words ("Due", "Today", "Tomorrow"). Unreadable text is shown as it came; no date is "".
 */
export function dueDateText(dueISO: string): string {
    const text = String(dueISO ?? "").trim();
    if (!text) return "";
    const date = parseDueDate(text);
    if (!date) return text;
    if (currentLanguage() === "en") return `${dayMonthText(date)} ${date.getFullYear()}`;

    return new Intl.DateTimeFormat(dateLocale(), { day: "numeric", month: "short", year: "numeric" }).format(date);
}

/**
 * A due BUCKET as a group heading, in the app's language.
 *
 * ⚠ The bucket VALUES ("Overdue", "Today", …) are keys — group keys (`due:Overdue`), the sort order,
 * the collapsed groups a device remembers — and must never be translated. Only this label is.
 */
export function dueBucketLabel(bucket: DueBucket): string {
    switch (bucket) {
        case "Overdue":
            return t("mobileTextDueOverdue", "Overdue");
        case "Today":
            return t("mobileTextDueToday", "Today");
        case "This week":
            return t("mobileTextDueThisWeek", "This week");
        case "None":
            return t("mobileTextDueNone", "No due date");
        default:
            return t("mobileTextDueLater", "Later");
    }
}

/**
 * A stored due value as a LOCAL calendar date at midnight.
 *
 * ⚠ `new Date("2026-08-29")` IS PARSED AS UTC — that is the ECMAScript spec for
 * a date-only string, and it is the bug this function exists to avoid. West of
 * Greenwich it lands on the evening of the 28th, so a todo due "today" would be
 * grouped as overdue for the whole working day. Split and construct instead,
 * which is unambiguously local.
 *
 * A full timestamp (`2026-08-29T22:00:00.000Z`) is parsed normally and then
 * reduced to the local day it falls on — the same question, asked of the
 * reader's calendar rather than of UTC.
 *
 * Returns null for anything unreadable, which callers must treat as undated
 * rather than as overdue.
 */
export function parseDueDate(value: string): Date | null {
    const text = String(value ?? "").trim();
    if (!text) return null;

    const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
    if (dateOnly) {
        const [, y, m, d] = dateOnly;
        const date = new Date(Number(y), Number(m) - 1, Number(d));
        // Rejects 2026-02-31, which the constructor would roll into March.
        return date.getMonth() === Number(m) - 1 ? date : null;
    }

    const stamp = Date.parse(text);
    if (Number.isNaN(stamp)) return null;

    const moment = new Date(stamp);
    return new Date(moment.getFullYear(), moment.getMonth(), moment.getDate());
}

/** Whole days from `now`'s local midnight to `date`. Negative is in the past. */
export function daysUntil(date: Date, now: number): number {
    const today = new Date(now);
    const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());

    // ⚠ Divide the DIFFERENCE OF TWO LOCAL MIDNIGHTS and round, never add 86.4e6
    // repeatedly: a DST change makes one of those days 23 or 25 hours long, and
    // truncation would put the day either side of a boundary out by one.
    return Math.round((date.getTime() - start.getTime()) / 86400000);
}

/**
 * Everything the screens need to know about a due date, at one instant.
 *
 * The rules mirror `pqfMobileDueBucket()` — the server's copy, still sent for a
 * phone on an older bundle — with ONE deliberate difference: a TODO with NO due
 * date is its own bucket, "None", here (2026-09-28), where the server still says
 * "Later". Only an older bundle reads the server's, and it never had "None".
 *
 * ⚠ An UNREADABLE value is "Later" and NOT overdue, and keeps its raw text as
 * the label. Calling it overdue would put a red date at the top of somebody's
 * list because of a parsing failure.
 */
export function dueFacts(dueISO: string, now: number): DueFacts {
    const text = String(dueISO ?? "").trim();
    // No due date is its OWN group, shown last — not "Later", which it used to share with dates far
    // ahead, making that group the biggest and least useful one on most lists.
    if (!text) return { label: "", bucket: "None", overdue: false };

    const date = parseDueDate(text);
    if (!date) return { label: text, bucket: "Later", overdue: false };

    const days = daysUntil(date, now);
    const dayMonth = dayMonthText(date);

    if (days < 0) return { label: dayMonth, bucket: "Overdue", overdue: true };
    if (days === 0) return { label: t("mobileTextDueToday", "Today"), bucket: "Today", overdue: false };

    const label = days === 1 ? t("mobileTextDueTomorrow", "Tomorrow") : dayMonth;

    return { label, bucket: days <= 7 ? "This week" : "Later", overdue: false };
}
